import {WorkbookFilterParams} from '../models/wordbook/workbookFilterParams';
import {wordbookApi} from '../api/wordbookApi';
import {CreateWordbookRequest, CreateWordbookResponse} from '../models/wordbook/createWordbookRequest';
import {UpdateWordbookRequest} from '../models/wordbook/updateWordbookRequest';
import {useBaseMutation, useBaseQuery, useInvalidateQueries, useNoDataMutation} from './useBaseQuery';
import {PaginationData} from '../models/paginationData';
import {
    BulkDeleteWordbooksResponse,
    BulkMoveToGroupResponse,
    BulkUpdateVisibilityResponse,
    LanguageStatsResponse,
    WorkbookOverviewStatsResponse,
    WorkbookStatsResponse
} from '../models/wordbook/wordbookResponses';
import {Visibility, Wordbook, WordbookDetail} from '../types/wordbook';

// Query Keys
export const wordbookKeys = {
    all: ['wordbook'] as const,
    lists: () => [...wordbookKeys.all, 'list'] as const,
    list: (filters: WorkbookFilterParams) => [...wordbookKeys.lists(), filters] as const,
    details: () => [...wordbookKeys.all, 'detail'] as const,
    detail: (id: number) => [...wordbookKeys.details(), id] as const,
    stats: () => [...wordbookKeys.all, 'stats'] as const,
    stat: (id: number) => [...wordbookKeys.stats(), id] as const,
    overview: () => [...wordbookKeys.stats(), 'overview'] as const,
    languages: () => [...wordbookKeys.stats(), 'languages'] as const,
};

// 목록 조회
export const useWordbookList = (params: WorkbookFilterParams) => {
    return useBaseQuery<PaginationData<Wordbook>>(
        wordbookKeys.list(params),
        () => wordbookApi.getList(params),
        {
            staleTime: 5 * 60 * 1000,
        }
    );
};

// 단건 조회
export const useWordbook = (id: number) => {
    return useBaseQuery<WordbookDetail>(
        wordbookKeys.detail(id),
        () => wordbookApi.getOne(id),
        {
            enabled: !!id,
        }
    );
};

// 생성
export const useCreateWordbook = () => {
    const {invalidate} = useInvalidateQueries();

    return useBaseMutation<CreateWordbookRequest, CreateWordbookResponse>(
        (data) => wordbookApi.create(data),
        {
            onSuccess: (_, variables) => {
                invalidate(wordbookKeys.lists());
                invalidate(wordbookKeys.overview());
                if (variables.groupId) {
                    invalidate(['wordbookGroup', variables.groupId]);
                }
            },
        }
    );
};

// 수정
export const useUpdateWordbook = () => {
    const {invalidateMultiple} = useInvalidateQueries();

    return useNoDataMutation<{ id: number; data: UpdateWordbookRequest }>(
        ({id, data}) => wordbookApi.update(id, data),
        {
            onSuccess: (_, variables) => {
                invalidateMultiple([
                    wordbookKeys.detail(variables.id),
                    wordbookKeys.lists()
                ]);
            },
        }
    );
};

// 삭제
export const useDeleteWordbook = () => {
    const {invalidate} = useInvalidateQueries();

    return useNoDataMutation<number>(
        (id) => wordbookApi.delete(id),
        {
            onSuccess: () => {
                invalidate(wordbookKeys.lists());
                invalidate(wordbookKeys.overview());
            },
        }
    );
};

// 영구 삭제
export const usePermanentDeleteWordbook = () => {
    const {invalidate} = useInvalidateQueries();

    return useNoDataMutation<number>(
        (id) => wordbookApi.permanentDelete(id),
        {
            onSuccess: () => {
                invalidate(wordbookKeys.lists());
                invalidate(wordbookKeys.overview());
            },
        }
    );
};

// 복원
export const useRestoreWordbook = () => {
    const {invalidateMultiple} = useInvalidateQueries();

    return useNoDataMutation<number>(
        (id) => wordbookApi.restore(id),
        {
            onSuccess: (_, id) => {
                invalidateMultiple([
                    wordbookKeys.detail(id),
                    wordbookKeys.lists()
                ]);
            },
        }
    );
};

// 노출 토글
export const useToggleWordbookVisibility = () => {
    const {invalidateMultiple} = useInvalidateQueries();

    return useNoDataMutation<number>(
        (id) => wordbookApi.toggleVisibility(id),
        {
            onSuccess: (_, id) => {
                invalidateMultiple([
                    wordbookKeys.detail(id),
                    wordbookKeys.lists()
                ]);
            },
        }
    );
};

// 그룹 변경
export const useChangeWordbookGroup = () => {
    const {invalidateMultiple} = useInvalidateQueries();

    return useNoDataMutation<{ id: number; groupId: number | null }>(
        ({id, groupId}) => wordbookApi.changeGroup(id, groupId),
        {
            onSuccess: (_, variables) => {
                invalidateMultiple([
                    wordbookKeys.detail(variables.id),
                    wordbookKeys.lists()
                ]);
            },
        }
    );
};

// 통계 조회
export const useWordbookStats = (id: number) => {
    return useBaseQuery<WorkbookStatsResponse>(
        wordbookKeys.stat(id),
        () => wordbookApi.getStats(id),
        {
            enabled: !!id,
        }
    );
};

// 일괄 삭제
export const useBulkDeleteWordbooks = () => {
    const {invalidate} = useInvalidateQueries();

    return useBaseMutation<number[], BulkDeleteWordbooksResponse>(
        (wordbookIds) => wordbookApi.bulkDelete(wordbookIds),
        {
            onSuccess: () => {
                invalidate(wordbookKeys.lists());
                invalidate(wordbookKeys.overview());
            },
        }
    );
};

// 일괄 그룹 이동
export const useBulkMoveToGroup = () => {
    const {invalidate} = useInvalidateQueries();

    return useBaseMutation<{ wordbookIds: number[]; targetGroupId: number | null }, BulkMoveToGroupResponse>(
        ({wordbookIds, targetGroupId}) => wordbookApi.bulkMoveToGroup(wordbookIds, targetGroupId),
        {
            onSuccess: () => {
                invalidate(wordbookKeys.lists());
            },
        }
    );
};

// 일괄 노출 설정
export const useBulkUpdateVisibility = () => {
    const {invalidate} = useInvalidateQueries();

    return useBaseMutation<{ wordbookIds: number[]; visibility: Visibility }, BulkUpdateVisibilityResponse>(
        ({wordbookIds, visibility}) => wordbookApi.bulkUpdateVisibility(wordbookIds, visibility),
        {
            onSuccess: () => {
                invalidate(wordbookKeys.lists());
            },
        }
    );
};

// 전체 통계
export const useWordbookOverviewStats = () => {
    return useBaseQuery<WorkbookOverviewStatsResponse>(
        wordbookKeys.overview(),
        () => wordbookApi.getOverviewStats(),
        {
            staleTime: 10 * 60 * 1000,
        }
    );
};

// 언어별 통계
export const useLanguageStats = () => {
    return useBaseQuery<LanguageStatsResponse>(
        wordbookKeys.languages(),
        () => wordbookApi.getLanguageStats(),
        {
            staleTime: 10 * 60 * 1000,
        }
    );
};
